import clsx from 'clsx';

/** Product name and line. One place, so the sign-in page, the top bar and the
 *  footer cannot drift apart. */
export const PRODUCT_NAME = 'Cerebro';
export const TAGLINE = 'Learning, amplified.';

/**
 * Where a logo sits. `chrome` is the slate top bar, which is slate in both
 * themes; `paper` follows the theme, so it swaps to the light-ink artwork
 * when the page goes dark.
 */
type Ground = 'chrome' | 'paper';

/**
 * Both artworks come from `cerebro-logo.png` at the repo root (regenerate with
 * `scripts/brand-assets.py`): the original
 * (charcoal and mint, for light grounds) and an `-on-dark` copy with the
 * charcoal half recoloured to the chrome ink. On paper both are rendered and
 * CSS (`.logo-light` / `.logo-dark` in index.css) shows the one that matches
 * the theme, so a pinned theme never flashes the wrong one.
 */
function ThemedImg({
  name,
  ground,
  alt,
  className,
}: {
  name: 'cerebro-logo' | 'cerebro-mark';
  ground: Ground;
  alt: string;
  className?: string;
}) {
  if (ground === 'chrome') {
    return <img src={`/${name}-on-dark.png`} alt={alt} className={clsx('shrink-0', className)} />;
  }
  return (
    <>
      <img src={`/${name}.png`} alt={alt} className={clsx('logo-light shrink-0', className)} />
      <img
        src={`/${name}-on-dark.png`}
        alt={alt}
        className={clsx('logo-dark shrink-0', className)}
      />
    </>
  );
}

/**
 * The Cerebro mark: the brain from the logo, on its own. Used where the full
 * logo will not fit (the top bar below 640px); `public/favicon.svg` carries
 * the same artwork.
 */
export function CerebroMark({
  size = 28,
  ground = 'paper',
  className,
}: {
  size?: number;
  ground?: Ground;
  className?: string;
}) {
  // Height drives the size; the brain is a little wider than tall.
  return (
    <span aria-hidden className={clsx('inline-flex shrink-0', className)} style={{ height: size }}>
      <ThemedImg
        name="cerebro-mark"
        ground={ground}
        alt=""
        className="h-full w-auto max-w-full object-contain object-left"
      />
    </span>
  );
}

/**
 * The full logo — mark, name and line — for the top bar and the sign-in page.
 * `showName={false}` drops to the mark alone for narrow top bars.
 */
export function Wordmark({
  onChrome = false,
  showName = true,
  className = 'h-9',
}: {
  /** True on the slate top bar; false on paper. */
  onChrome?: boolean;
  /** Below 640px the top bar keeps only the mark. */
  showName?: boolean;
  /** Sets the height; the width follows the artwork. */
  className?: string;
}) {
  const ground: Ground = onChrome ? 'chrome' : 'paper';
  if (!showName) return <CerebroMark size={28} ground={ground} />;
  return (
    <span className={clsx('inline-flex shrink-0', className)}>
      <ThemedImg
        name="cerebro-logo"
        ground={ground}
        alt={PRODUCT_NAME}
        className="h-full w-auto max-w-full object-contain object-left"
      />
    </span>
  );
}
